import React, { useEffect, useState } from "react";
import withRouter from "components/Common/withRouter";
import {
  Card,
  CardBody,
  Col,
  Container,
  Row,
  Badge,
  Spinner
} from "reactstrap";
import Breadcrumbs from "components/Common/Breadcrumb";
import moment from "moment";

const ODATA_ANALYTICS = "https://mansethaberapi.stechomeyazilim.com/app_analytics?$format=json&$top=10000";

const AnalyticsDashboard = () => {
  document.title = "Uygulama İstatistikleri | Manşet Haber ADMIN PANELI";

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const res = await fetch(ODATA_ANALYTICS);
      const json = await res.json();
      const rows = json.value || [];

      // ── Aggregate hesaplamalar (frontend'de) ──
      const now = moment();
      const last24h = now.clone().subtract(24, "hours");
      const last7days = now.clone().subtract(7, "days");

      // Ekran bazında
      const screenMap = {};
      rows.forEach((r) => {
        const name = r.screen_name || "Bilinmeyen";
        const sec = Number(r.duration_seconds) || 0;
        if (!screenMap[name]) {
          screenMap[name] = { visits: 0, total_seconds: 0 };
        }
        screenMap[name].visits += 1;
        screenMap[name].total_seconds += sec;
      });
      const screens = Object.entries(screenMap)
        .map(([screen_name, stats]) => ({
          screen_name,
          visits: stats.visits,
          total_seconds: stats.total_seconds,
          avg_seconds: Math.round(stats.total_seconds / stats.visits),
        }))
        .sort((a, b) => b.total_seconds - a.total_seconds);

      // Son 24 saat
      const rows24h = rows.filter((r) =>
        moment(r.created_at).isAfter(last24h)
      );
      const last24hStats = {
        visits_24h: rows24h.length,
        total_seconds_24h: rows24h.reduce((sum, r) => sum + (Number(r.duration_seconds) || 0), 0),
      };

      // Son 7 gün günlük
      const dailyMap = {};
      rows
        .filter((r) => moment(r.created_at).isAfter(last7days))
        .forEach((r) => {
          const d = moment(r.created_at).format("YYYY-MM-DD");
          const sec = Number(r.duration_seconds) || 0;
          if (!dailyMap[d]) dailyMap[d] = { visits: 0, total_seconds: 0 };
          dailyMap[d].visits += 1;
          dailyMap[d].total_seconds += sec;
        });
      const daily = Object.entries(dailyMap)
        .map(([date, stats]) => ({ date, ...stats }))
        .sort((a, b) => b.date.localeCompare(a.date));

      // Toplam
      const totalStats = {
        total_visits: rows.length,
        total_seconds: rows.reduce((sum, r) => sum + (Number(r.duration_seconds) || 0), 0),
      };

      setData({
        screens,
        last_24h: last24hStats,
        daily,
        total: totalStats,
      });
    } catch (error) {
      console.error("Analytics verileri alınamadı:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const formatDuration = (seconds) => {
    if (!seconds || seconds <= 0) return "0 sn";
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    if (m >= 60) {
      const h = Math.floor(m / 60);
      const rm = m % 60;
      return `${h} sa ${rm} dk`;
    }
    return `${m} dk ${s} sn`;
  };

  const totalVisits = data?.total?.total_visits || 0;
  const totalSeconds = data?.total?.total_seconds || 0;
  const visits24h = data?.last_24h?.visits_24h || 0;
  const seconds24h = data?.last_24h?.total_seconds_24h || 0;

  const statCards = [
    { title: "Toplam Ziyaret", value: totalVisits.toLocaleString("tr-TR"), icon: "bx-mobile-alt", color: "bg-primary" },
    { title: "Toplam Ekran Süresi", value: formatDuration(totalSeconds), icon: "bx-time", color: "bg-success" },
    { title: "Son 24s Ziyaret", value: visits24h.toLocaleString("tr-TR"), icon: "bx-trending-up", color: "bg-info" },
    { title: "Son 24s Süre", value: formatDuration(seconds24h), icon: "bx-stopwatch", color: "bg-warning" },
  ];

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Yönetim" breadcrumbItem="Uygulama İstatistikleri" />

          <Row>
            {statCards.map((card, key) => (
              <Col md="3" key={key}>
                <Card className="mini-stats-wid">
                  <CardBody>
                    <div className="d-flex">
                      <div className="flex-grow-1">
                        <p className="text-muted fw-medium mb-1">{card.title}</p>
                        {loading ? (
                          <Spinner size="sm" color="primary" />
                        ) : (
                          <h4 className="mb-0">{card.value}</h4>
                        )}
                      </div>
                      <div className={`avatar-sm rounded-circle ${card.color} align-self-center mini-stat-icon`}>
                        <span className={`avatar-title rounded-circle ${card.color}`}>
                          <i className={`bx ${card.icon} font-size-24`} />
                        </span>
                      </div>
                    </div>
                  </CardBody>
                </Card>
              </Col>
            ))}
          </Row>

          <Row>
            <Col xl="8">
              <Card>
                <CardBody>
                  <h5 className="card-title mb-4">
                    <i className="bx bx-desktop me-2 text-primary" />
                    Ekran Bazında İstatistikler
                  </h5>
                  {loading ? (
                    <div className="text-center"><Spinner color="primary" /></div>
                  ) : !data || !data.screens || data.screens.length === 0 ? (
                    <p className="text-muted">Henüz veri bulunmamaktadır.</p>
                  ) : (
                    <div className="table-responsive">
                      <table className="table table-hover table-nowrap mb-0">
                        <thead className="table-light">
                          <tr>
                            <th>Ekran</th>
                            <th className="text-end">Ziyaret</th>
                            <th className="text-end">Toplam Süre</th>
                            <th className="text-end">Ort. Süre</th>
                          </tr>
                        </thead>
                        <tbody>
                          {data.screens.map((screen, idx) => (
                            <tr key={idx}>
                              <td>
                                <strong>{screen.screen_name}</strong>
                              </td>
                              <td className="text-end">
                                <Badge color="primary" className="font-size-11">
                                  {screen.visits}
                                </Badge>
                              </td>
                              <td className="text-end">
                                <Badge color="success" className="font-size-11">
                                  {formatDuration(screen.total_seconds)}
                                </Badge>
                              </td>
                              <td className="text-end">
                                <Badge color="info" className="font-size-11">
                                  {formatDuration(screen.avg_seconds)}
                                </Badge>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>

            <Col xl="4">
              <Card>
                <CardBody>
                  <h5 className="card-title mb-4">
                    <i className="bx bx-calendar me-2 text-success" />
                    Son 7 Gün
                  </h5>
                  {loading ? (
                    <div className="text-center"><Spinner color="primary" /></div>
                  ) : !data || !data.daily || data.daily.length === 0 ? (
                    <p className="text-muted">Henüz veri bulunmamaktadır.</p>
                  ) : (
                    <div className="table-responsive">
                      <table className="table table-hover table-nowrap mb-0">
                        <thead className="table-light">
                          <tr>
                            <th>Tarih</th>
                            <th className="text-end">Ziyaret</th>
                            <th className="text-end">Süre</th>
                          </tr>
                        </thead>
                        <tbody>
                          {data.daily.map((day, idx) => (
                            <tr key={idx}>
                              <td>{moment(day.date).format("DD.MM.YYYY")}</td>
                              <td className="text-end">
                                <Badge color="primary" className="font-size-11">
                                  {day.visits}
                                </Badge>
                              </td>
                              <td className="text-end">
                                <Badge color="success" className="font-size-11">
                                  {formatDuration(day.total_seconds)}
                                </Badge>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default withRouter(AnalyticsDashboard);
